import { Trans, useTranslation } from "react-i18next";
import { useCurrentFrameSettings, useIsFullscreen } from "../hooks";
import { useAppSelector, useAppDispatch } from "../store";
import { useCallback, useEffect, useRef, useState } from "react";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import useUtmify from "../utm";
import { useFrameStorage } from "../storage";
import { AudioRecorder, VoiceMessageRecording } from "./AudioRecorder";
import ChatInput from "./ChatInput";
import { Attachments, FileAttachment } from "./Attachments";
import { uploadFile } from "../actions/sharedActions";
import { toast } from "sonner";
import PreviewFile from "./PreviewFile";
import { FileData } from "../types/api";
import { v4 as uuidv4 } from "uuid";
import mime from "mime";

const ACCEPT_IMAGE = ".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.gif"; // no .svg
// NOTE(memben): disallow audio as chrome on android will ask for permission to record audio
// const ACCEPT_AUDIO = ".mp3,.ogg,.opus"; // NOTE(memben): subset only, remove to disallow videos "audio/mp4,.mp4,.mp3,.ogg,.opus,.webm" mp4 for safari
const ACCEPT_DOCS =
  "application/pdf,application/msword,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.xml,.json,.pptx,.ppt,.odt,.ods,.odp,.rtf,.html,.md,.yaml,.yml";

interface PromptProps {
  value: string;
  setValue: (value: string) => void;
  onSubmit: (value: string, files?: FileData[]) => void;
  isLoading: boolean;
  isDisabled: boolean;
}

export function Prompt({
  value,
  setValue,
  onSubmit,
  isLoading,
  isDisabled,
}: PromptProps) {
  const { t } = useTranslation();
  const utmify = useUtmify();
  const dispatch = useAppDispatch();
  const isFullscreen = useIsFullscreen();
  const isOpen = useAppSelector((state) => state.launcher.isOpen);
  const lastOpen = useAppSelector((state) => state.launcher.lastOpen);
  const { data: settings } = useCurrentFrameSettings();
  const frameStorage = useFrameStorage(
    useAppSelector((state) => state.config.data.frameId)
  );
  const hasSession = frameStorage.sessionId !== undefined;

  let accepted_file_input = ACCEPT_DOCS;
  if (settings?.allow_image_upload) {
    accepted_file_input += "," + ACCEPT_IMAGE;
  }
  const acceptedExtsAndMimeTypes = accepted_file_input.split(",");

  const isFileSupported = useCallback(
    (file: File): boolean => {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      return (
        acceptedExtsAndMimeTypes.includes(file.type) ||
        acceptedExtsAndMimeTypes.includes(ext)
      );
    },
    [acceptedExtsAndMimeTypes]
  );

  // State for audio recording mode
  const [isAudioMode, setIsAudioMode] = useState(false);
  // State for file attachments
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  // State for drag overlay
  const [isDragging, setIsDragging] = useState(false);
  // State for preview modal
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    type: string;
    contentType: string;
    name: string;
  } | null>(null);

  const disabled = isDisabled;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const inEditor = performance.now() - lastOpen > 100;
    if (textareaRef.current && !inEditor) {
      if (isOpen) textareaRef.current.focus();
      else textareaRef.current.blur();
    }
  }, [isOpen, lastOpen]);

  // Handle form submission
  const handleSubmit = () => {
    onSubmit(value); // attachments will be handled via redux store ^^
    setValue("");
    setAttachments([]);
  };

  const handleAudioSubmit = (message: VoiceMessageRecording) => {
    const uploadAndSend = async () => {
      const file = new File([message.data], message.name, {
        type: message.content_type,
      });
      try {
        const voiceFile = await dispatch(
          uploadFile({
            file,
            type: message.type,
          })
        ).unwrap();
        onSubmit(value, [voiceFile]);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        console.error("ERROR", error, (error as any)?.errors);
        toast.error(`Failed to upload voice message: ${message}`);
      } finally {
        setIsAudioMode(false);
      }
    };
    uploadAndSend();
    setAttachments([]); // NOTE(liamvdv): should be empty anyways
    setValue(""); // NOTE(liamvdv): should be empty anyways
  };

  // // Mock download for audio (temporary implementation)
  // const mockDownload = (audioBlob: Blob) => {
  //   const url = URL.createObjectURL(audioBlob);
  //   const a = document.createElement("a");

  //   // Generate filename with date and time
  //   const date = new Date();
  //   const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  //   const formattedTime = `${String(date.getHours()).padStart(2, "0")}-${String(date.getMinutes()).padStart(2, "0")}-${String(date.getSeconds()).padStart(2, "0")}`;
  //   const filename = `recording_${formattedDate}_${formattedTime}.webm`;

  //   a.href = url;
  //   a.download = filename;
  //   a.click();

  //   URL.revokeObjectURL(url);
  // };

  // Audio recorder callbacks

  const switchToAudioMode = () => {
    setIsAudioMode(true);
  };

  const cancelAudioMode = () => {
    setIsAudioMode(false);
  };

  // Determine file type category for upload
  const getFileTypeCategory = (file: File): "image" | "audio" | "document" => {
    if (file.type.startsWith("image/")) {
      return "image";
    } else if (file.type.startsWith("audio/")) {
      return "audio";
    }
    return "document";
  };

  // Upload file and update attachment status
  const uploadAttachment = async (file: File, temporaryId: string) => {
    const fileType = getFileTypeCategory(file);
    const contentType = file.type || mime.getType(file.name) || "";
    const abortController = new AbortController();

    // Add the AbortController to the attachment
    setAttachments((prev) => {
      return prev.map((attachment) => {
        if (attachment.temporary_id === temporaryId) {
          return {
            ...attachment,
            uploadController: abortController,
          };
        }
        return attachment;
      });
    });

    return toast.promise(
      async () => {
        try {
          const result = await dispatch(
            uploadFile({
              file: new File([file], file.name, { type: contentType }),
              type: fileType,
              signal: abortController.signal,
            })
          ).unwrap();

          // Update the attachment with the server ID and success status
          setAttachments((prev) => {
            return prev.map((attachment) => {
              if (attachment.temporary_id === temporaryId) {
                return {
                  ...attachment,
                  id: result.id,
                  status: "success",
                };
              }
              return attachment;
            });
          });

          return result;
        } catch (error) {
          // Check if the error is due to an aborted request
          if ((error as any)?.name === "AbortError") {
            console.log(`Upload of ${file.name} was cancelled`);
            return { cancelled: true };
          }

          // Update the attachment status to error
          setAttachments((prev) => {
            return prev.map((attachment) => {
              if (attachment.temporary_id === temporaryId) {
                return {
                  ...attachment,
                  status: "error",
                  error:
                    error instanceof Error ? error.message : "Unknown error",
                };
              }
              return attachment;
            });
          });

          // Rethrow to let toast.promise handle the error
          throw error;
        }
      },
      {
        loading: `Uploading ${file.name}...`,
        success: (result) => {
          if ((result as any)?.cancelled) {
            return `Upload of ${file.name} cancelled`;
          }
          return `${file.name} uploaded successfully`;
        },
        error: (err) => {
          let msg = `${file.name} upload failed:\n`;
          if ("message" in err && err.message.startsWith("{")) {
            const { errors } = JSON.parse(err.message);
            msg += errors.map((e: any) => e.message).join("\n");
          } else {
            const errorMessage =
              err instanceof Error ? err.message : "Unknown error";
            msg += errorMessage;
          }
          return msg;
        },
      }
    );
  };

  // Handle file attachments with temporary IDs
  const handleFileAttach = useCallback(
    (files: FileList | File[]) => {
      if (!files.length || disabled || isAudioMode) return;

      // Check if adding these files would exceed the limit
      const availableSlots = 10 - attachments.length;

      if (availableSlots <= 0) {
        // No slots available
        toast.error("Maximum of 10 files allowed", {
          duration: 6000,
        });
        return;
      }

      const newAttachments: FileAttachment[] = [];
      Array.from(files).forEach((file) => {
        if (!isFileSupported(file)) {
          const isImageFile = file.type.startsWith("image/");
          const errorMessage =
            isImageFile && !settings?.allow_image_upload
              ? `Image uploads are not allowed: ${file.name}`
              : `File type not supported: ${file.name}`;
          toast.error(errorMessage, {
            duration: 10000,
          });
          return;
        }
        if (file.size > 100 * 1024 * 1024) {
          toast.error(`File size exceeds 100MB limit: ${file.name}`, {
            duration: 10000,
          });
          return;
        }
        if (newAttachments.length >= availableSlots) {
          toast.error(`File limit reached, skipping ${file.name}`);
          return;
        }
        // Generate a unique temporary ID
        const temporaryId = uuidv4();

        // Create a preview for the file
        const fileAttachment: FileAttachment = {
          file,
          type: getFileTypeCategory(file),
          content_type: file.type || mime.getType(file.name) || "",
          preview: URL.createObjectURL(file),
          status: "uploading",
          temporary_id: temporaryId,
        };

        newAttachments.push(fileAttachment);
      });

      // First update state with uploading status
      setAttachments((prev) => [...prev, ...newAttachments]);

      // Then initiate uploads for each file
      newAttachments.forEach((attachment) => {
        uploadAttachment(attachment.file, attachment.temporary_id).catch(
          (error) => {
            // This catch is just a safeguard as the toast.promise already handles errors
            console.error(`Error uploading ${attachment.file.name}:`, error);
          }
        );
      });
    },
    [
      disabled,
      isAudioMode,
      uploadAttachment,
      attachments.length,
      isFileSupported,
      settings?.allow_image_upload,
    ]
  );

  // Handle file paste from clipboard
  const handlePasteFiles = useCallback(
    (files: FileList) => {
      if (files.length > 0 && !isAudioMode) {
        handleFileAttach(files);
      }
    },
    [isAudioMode, handleFileAttach]
  );

  // Handle file selection
  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current?.files?.length) {
      handleFileAttach(fileInputRef.current.files);
      // Reset the input so the same file can be selected again
      fileInputRef.current.value = "";
    }
  }, [handleFileAttach]);

  // Close file preview
  const closeFilePreview = () => {
    setPreviewFile(null);
  };

  // Trigger file input click
  const openFileDialog = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Set up drag and drop for files
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isAudioMode) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if we're leaving the main container (not just entering a child)
      if (e.currentTarget === dropZoneRef.current) {
        const rect = dropZoneRef.current?.getBoundingClientRect();
        if (rect) {
          const x = e.clientX;
          const y = e.clientY;

          // Check if pointer is outside the element
          if (
            x <= rect.left ||
            x >= rect.right ||
            y <= rect.top ||
            y >= rect.bottom
          ) {
            setIsDragging(false);
          }
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!disabled && !isAudioMode && e.dataTransfer?.files) {
        handleFileAttach(e.dataTransfer.files);
      }
    };

    // Handle when drag operation ends without dropping
    const handleDragEnd = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const container = dropZoneRef.current;
    if (container) {
      container.addEventListener("dragover", handleDragOver);
      container.addEventListener("dragleave", handleDragLeave);
      container.addEventListener("drop", handleDrop);
      container.addEventListener("dragend", handleDragEnd);

      return () => {
        container.removeEventListener("dragover", handleDragOver);
        container.removeEventListener("dragleave", handleDragLeave);
        container.removeEventListener("drop", handleDrop);
        container.removeEventListener("dragend", handleDragEnd);
      };
    }
  }, [disabled, isAudioMode, handleFileAttach]);

  // Handle keydown events for modal (Escape key to close)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && previewFile) {
        closeFilePreview();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewFile]);

  return (
    <div className="no-prose w-full md:bg-gradient-to-t from-chat from-50% bg-transparent dark:md:bg-vert-dark-gradient z-10">
      <div className="flex flex-col bg-chat rounded-t-[32px] group">
        <form className="w-full relative" onSubmit={(e) => e.preventDefault()}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            className="hidden"
            accept={accepted_file_input}
            disabled={disabled}
          />

          {/* Main content area that serves as drop zone */}
          <div
            ref={dropZoneRef}
            className={`rounded-[24px] group-focus-within:ring-2 ${
              isFullscreen ? "group-focus-within:mb-2" : ""
            } ring-brand bg-slate-100 relative`}
          >
            {/* Attachments Component */}
            <Attachments
              attachments={attachments}
              setAttachments={setAttachments}
              disabled={disabled}
              isAudioMode={isAudioMode}
              isDragging={isDragging}
              onPreviewFile={setPreviewFile}
            />

            <div className="flex w-full items-center p-1.5">
              <div className="flex items-end w-full gap-1.5">
                {/* Audio Recorder Mode */}
                {attachments.length === 0 && isAudioMode ? (
                  <AudioRecorder
                    onAudioRecorded={handleAudioSubmit}
                    onCancel={cancelAudioMode}
                    maxDuration={30 * 60 * 1000}
                  />
                ) : (
                  /* Text Input Mode */
                  <>
                    <button
                      type="button"
                      onClick={openFileDialog}
                      disabled={disabled}
                      className="p-2.5 text-chat-text hover:bg-slate-200 transition-colors rounded-full disabled:opacity-50"
                      aria-label="Attach file"
                    >
                      <PaperClipIcon className="h-5 w-5" />
                    </button>

                    <ChatInput
                      value={value}
                      onChange={setValue}
                      onSubmit={handleSubmit}
                      isDisabled={disabled}
                      isSubmittable={
                        value.trim().length > 0 ||
                        (attachments.length > 0 &&
                          attachments.every((a) => a.status === "success")) // NOTE(liamvdv): force deletion of failed attachments
                      }
                      isSubmitDisabled={disabled || isLoading}
                      placeholder={t("prompt.placeholder")}
                      onPasteFiles={handlePasteFiles}
                      onSwitchToAudio={switchToAudioMode}
                      isAudioDisabled={attachments.length > 0}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* Footer with legal text or powered by info */}
        <div
          className={`block ${isFullscreen ? "group-focus-within:hidden" : ""}`}
        >
          {!hasSession ? (
            <p className="text-xs text-center text-chat-text-light mt-1 mb-1">
              <Trans
                i18nKey="legal"
                components={{
                  tosLink: (
                    <a
                      href={
                        settings.terms_of_service_url ??
                        import.meta.env.VITE_TERMS_OF_SERVICE_URL
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="underline link focus:outline-brand"
                    />
                  ),
                  ppLink: (
                    <a
                      href={
                        settings.privacy_policy_url ??
                        import.meta.env.VITE_PRIVACY_POLICY_URL
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="underline link focus:outline-brand"
                    />
                  ),
                }}
              />
            </p>
          ) : (
            <p className="text-xs text-center text-chat-text-light mt-1 mb-1">
              <Trans
                i18nKey="powered_by"
                values={{
                  assistant_name: settings?.assistant_name ?? t("assistant"),
                }}
                components={{
                  botBrainsLink: (
                    <a
                      href={utmify(
                        "https://www.botbrains.io",
                        "powered_by_link"
                      )}
                      target="_blank"
                      className="focus:outline-brand font-semibold hover:underline"
                    />
                  ),
                }}
              />
            </p>
          )}
        </div>
      </div>

      {previewFile && (
        <PreviewFile
          previewFile={previewFile}
          closeFilePreview={closeFilePreview}
        />
      )}
    </div>
  );
}
